export interface TournamentNameColours {
  [key: string]: string;
}

export interface SanitizerOptions {
  enableCaching?: boolean;
  maxCacheSize?: number;
}

const SANITIZATION_PATTERNS = {
  eventHandlers: /\s+on\w+\s*=\s*[^>\s]*(?:"[^"]*"|'[^']*'|[^\s>]*)/gi,
  javascriptProtocol: /javascript\s*:/gi,
  dataProtocol: /data\s*:/gi,
  scriptTags: /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
  styleTags: /<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi,
  dangerousTags:
    /<(iframe|object|embed|form|input|textarea|select|option|img|link|meta|base|title|head)\b[^>]*\/?>/gi,
  fontWithColor: /<font\s+color\s*=\s*([^>\s]*?)(?:\s[^>]*)?>([^]*?)<\/font>/gi,
  remainingFontTags: /<\/?font[^>]*>/gi,
  cleanAllowedTags: /<(b|i|u|strong|em)(?:\s[^>]*)?>/gi,
  spanStyleExtract: /style\s*=\s*["']?color\s*:\s*([^;"']+)["']?/i,
  hexColor: /^#([0-9A-F]{3}){1,2}$/i,
  safeColorValue: /^(#[0-9A-F]{3,6}|[a-zA-Z-]+)$/i,
  allowedTagCheck: /^(div|a|font|b|i|u|span|strong|em)$/i
} as const;

const sanitizationCache = new Map<string, string>();

const colorValidationCache = new Map<string, boolean>();

function validateColor(colorValue: string): boolean {
  const cached = colorValidationCache.get(colorValue);
  if (cached !== undefined) return cached;

  const isValid = SANITIZATION_PATTERNS.safeColorValue.test(colorValue);

  if (colorValidationCache.size > 500) {
    colorValidationCache.clear();
  }

  colorValidationCache.set(colorValue, isValid);
  return isValid;
}

function performSanitization(html: string, tournamentNameColours: TournamentNameColours): string {
  if (!html.includes('<')) {
    return html;
  }

  let sanitized = html;

  sanitized = sanitized
    .replace(SANITIZATION_PATTERNS.scriptTags, '')
    .replace(SANITIZATION_PATTERNS.styleTags, '')
    .replace(SANITIZATION_PATTERNS.dangerousTags, '');

  sanitized = sanitized.replace(
    /<([a-zA-Z][a-zA-Z0-9]*)\b([^>]*)>/gi,
    (match, tagName, attributes) => {
      if (!SANITIZATION_PATTERNS.allowedTagCheck.test(tagName)) {
        return '';
      }

      let cleanAttributes = attributes
        .replace(SANITIZATION_PATTERNS.eventHandlers, '')
        .replace(SANITIZATION_PATTERNS.javascriptProtocol, '')
        .replace(SANITIZATION_PATTERNS.dataProtocol, '');

      if (tagName !== 'span' && tagName !== 'font' && tagName !== 'a') {
        cleanAttributes = '';
      }

      if (tagName === 'a' && cleanAttributes.includes('href')) {
        const hrefMatch = cleanAttributes.match(/href\s*=\s*("[^"]*"|'[^']*'|[^\s>]*)/i);
        if (hrefMatch) {
          cleanAttributes = ' href=""';
        }
      }

      return `<${tagName}${cleanAttributes}>`;
    }
  );

  sanitized = sanitized.replace(/<\/([a-zA-Z][a-zA-Z0-9]*)\s*>/gi, (match, tagName) => {
    if (SANITIZATION_PATTERNS.allowedTagCheck.test(tagName)) {
      return match;
    }
    return '';
  });

  if (Object.keys(tournamentNameColours).length > 0) {
    sanitized = sanitized.replace(SANITIZATION_PATTERNS.fontWithColor, (_, colorValue, content) => {
      const cleanColor = colorValue.replace(/['"]/g, '');

      if (cleanColor.startsWith('#')) {
        return SANITIZATION_PATTERNS.hexColor.test(cleanColor)
          ? `<span style="color: ${cleanColor}">${content}</span>`
          : content;
      }

      const color = tournamentNameColours[cleanColor];
      return color ? `<span style="color: ${color}">${content}</span>` : content;
    });
  } else {
    sanitized = sanitized.replace(
      /<font\s+color\s*=\s*["']?(#[0-9A-F]{3,6})["']?[^>]*>([^]*?)<\/font>/gi,
      (_, colorValue, content) => `<span style="color: ${colorValue}">${content}</span>`
    );
  }

  sanitized = sanitized.replace(SANITIZATION_PATTERNS.remainingFontTags, '');

  sanitized = sanitized.replace(SANITIZATION_PATTERNS.cleanAllowedTags, '<$1>');

  sanitized = sanitized.replace(/<span(?:\s[^>]*)?>/gi, (match) => {
    const styleMatch = match.match(SANITIZATION_PATTERNS.spanStyleExtract);
    if (styleMatch) {
      const colorValue = styleMatch[1].trim();
      return validateColor(colorValue) ? `<span style="color: ${colorValue}">` : '<span>';
    }
    return '<span>';
  });

  return sanitized;
}

export function sanitizeHTML(
  html: string,
  tournamentNameColours: TournamentNameColours = {},
  options: SanitizerOptions = {}
): string {
  const { enableCaching = true, maxCacheSize = 1000 } = options;

  if (!html || typeof html !== 'string') {
    return '';
  }

  let result: string;

  if (enableCaching) {
    const cached = sanitizationCache.get(html);
    if (cached !== undefined) return cached;

    result = performSanitization(html, tournamentNameColours);

    if (sanitizationCache.size >= maxCacheSize) {
      const firstKey = sanitizationCache.keys().next().value;
      if (firstKey !== undefined) {
        sanitizationCache.delete(firstKey);
      }
    }

    sanitizationCache.set(html, result);
  } else {
    result = performSanitization(html, tournamentNameColours);
  }

  return result;
}

export function clearSanitizerCache(): void {
  sanitizationCache.clear();
  colorValidationCache.clear();
}

export function getCacheStats(): { sanitizationCacheSize: number; colorCacheSize: number } {
  return {
    sanitizationCacheSize: sanitizationCache.size,
    colorCacheSize: colorValidationCache.size
  };
}
